import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Image as ImageIcon } from "lucide-react";
import { api, apiUrl, authToken } from "../api";
import { Button, StatusBadge, statusLabel, EmptyState } from "../ui";
import { useAuthedImage } from "../hooks/useAuthedImage";

interface Manifest {
  presentation_id: string;
  status: string;
  page_count: number;
  has_pdf: boolean;
  has_pptx: boolean;
  preview_generated_at: string | null;
}

interface Presentation {
  id: string;
  project_id: string;
  title: string;
  status: string;
  audience: string | null;
  brief: string | null;
  pdf_object_key: string | null;
  pptx_object_key: string | null;
  preview_page_count: number | null;
  preview_generated_at: string | null;
  plan: Record<string, unknown> | null;
  model: string | null;
  tokens_in: number;
  tokens_out: number;
}

// CostBreakdown: kommt vom Backend-Endpoint /presentations/<id>/cost-breakdown.
// Liest UsageEvents wo extra_json.presentation_id == pres.id und aggregiert.
// Text-Cost wird aus pres.tokens × model_pricing berechnet (siehe app/llm/pricing.py).
interface CostBreakdown {
  presentation_id: string;
  text_cost_eur: number;
  text_tokens_in: number;
  text_tokens_out: number;
  text_model: string | null;
  image_gen_cost_eur: number;
  image_gen_count: number;
  image_audit_cost_eur: number;
  image_audit_count: number;
  total_eur: number;
}

type Tab = "preview" | "slides" | "audit";

function PageThumbnail({
  presId,
  pageN,
  onClick,
}: {
  presId: string;
  pageN: number;
  onClick: () => void;
}) {
  const { src, loading, error } = useAuthedImage(
    `/presentations/${presId}/preview/page/${pageN}`,
  );
  return (
    <button
      onClick={onClick}
      className="group relative aspect-video overflow-hidden rounded-lg border border-border bg-surface-2 transition hover:border-accent-500 hover:shadow-md"
    >
      {loading && (
        <div className="flex h-full items-center justify-center text-xs text-text-3">
          ...
        </div>
      )}
      {error && (
        <div className="flex h-full items-center justify-center text-xs text-danger">
          {error}
        </div>
      )}
      {src && (
        <img
          src={src}
          alt={`Slide ${pageN}`}
          width={1920}
          height={1080}
          className="h-full w-full object-contain"
          loading="lazy"
        />
      )}
      <div className="absolute bottom-1 left-1 rounded-md bg-black/60 px-1.5 py-0.5 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100">
        {pageN}
      </div>
    </button>
  );
}

function Lightbox({
  presId,
  pageN,
  total,
  onClose,
  onPrev,
  onNext,
}: {
  presId: string;
  pageN: number;
  total: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { src, loading } = useAuthedImage(
    `/presentations/${presId}/preview/page/${pageN}`,
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6"
      onClick={onClose}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        disabled={pageN <= 1}
        className="absolute left-4 rounded-full bg-surface/10 p-3 text-white hover:bg-surface/20 disabled:opacity-30"
      >
        ‹
      </button>
      <div
        className="relative max-h-full max-w-7xl"
        onClick={(e) => e.stopPropagation()}
      >
        {loading && <div className="text-white">Lade Slide {pageN}…</div>}
        {src && (
          <img
            src={src}
            alt={`Slide ${pageN}`}
            width={1920}
            height={1080}
            className="max-h-[90vh] max-w-full rounded-lg shadow-2xl"
          />
        )}
        <div className="absolute -bottom-8 left-0 right-0 text-center text-sm text-white/80">
          {pageN} / {total}
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        disabled={pageN >= total}
        className="absolute right-4 rounded-full bg-surface/10 p-3 text-white hover:bg-surface/20 disabled:opacity-30"
      >
        ›
      </button>
      <button
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-surface/10 p-2 text-white hover:bg-surface/20"
      >
        ✕
      </button>
    </div>
  );
}

export default function PresentationDetailPage() {
  const { presentationId } = useParams<{ presentationId: string }>();
  const navigate = useNavigate();
  const [pres, setPres] = useState<Presentation | null>(null);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [cost, setCost] = useState<CostBreakdown | null>(null);
  const [tab, setTab] = useState<Tab>("preview");
  const [lightboxPage, setLightboxPage] = useState<number | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    if (!presentationId) return;
    try {
      const [p, m] = await Promise.all([
        api.get<Presentation>(`/presentations/${presentationId}`),
        api.get<Manifest>(
          `/presentations/${presentationId}/preview/manifest`,
        ),
      ]);
      setPres(p);
      setManifest(m);
      setErr(null);
      // Cost-Breakdown nachladen, fail-soft (Endpoint kann fuer aelteren
      // Decks 0 zurueckgeben weil pres_id-Stempel erst seit 2026-06-28).
      try {
        const c = await api.get<CostBreakdown>(
          `/presentations/${presentationId}/cost-breakdown`,
        );
        setCost(c);
      } catch {
        setCost(null);
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [presentationId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Poll manifest while preview is being generated
  useEffect(() => {
    if (!manifest) return;
    if (manifest.page_count > 0) return;
    if (pres?.status === "error") return;
    const t = setInterval(loadAll, 3000);
    return () => clearInterval(t);
  }, [manifest, pres, loadAll]);

  const downloadPptx = async () => {
    if (!pres) return;
    const t = authToken();
    const r = await fetch(apiUrl(`/presentations/${pres.id}/pptx`), {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${pres.title}.pptx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    if (!pres) return;
    const t = authToken();
    const r = await fetch(apiUrl(`/presentations/${pres.id}/preview/pdf`), {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${pres.title}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const regenerate = async () => {
    if (!pres) return;
    setRegenerating(true);
    setErr(null);
    try {
      await api.post(`/presentations/${pres.id}/preview/regenerate`);
      await loadAll();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setRegenerating(false);
    }
  };

  const slidesSpec = useMemo(() => {
    const storyboard = (pres?.plan as { storyboard?: unknown[] } | null)
      ?.storyboard;
    return Array.isArray(storyboard) ? storyboard : null;
  }, [pres?.plan]);

  const gateReport = useMemo(() => {
    const r = (pres?.plan as { substance_gate_report?: unknown[] } | null)
      ?.substance_gate_report;
    return Array.isArray(r) ? r : null;
  }, [pres?.plan]);

  if (err) {
    return (
      <div className="p-6 text-danger">Fehler: {err}</div>
    );
  }
  if (!pres || !manifest) {
    return <div className="p-6 text-text-2">Lädt…</div>;
  }

  const pageCount = manifest.page_count;
  const previewReady = pageCount > 0;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-border bg-surface px-6 py-4">
        <div className="mb-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/p/${pres.project_id}`)}
            className="text-sm text-text-2 hover:text-text"
          >
            ← Projekt
          </button>
          <span className="text-text-3">/</span>
          <span className="text-sm text-text-2">Präsentation</span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-semibold text-text">
              {pres.title}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-text-2">
              <StatusBadge status={statusLabel(pres.status).status}>
                {statusLabel(pres.status).label}
              </StatusBadge>
              {pres.audience && <span>Zielgruppe: {pres.audience}</span>}
              {pageCount > 0 && <span>{pageCount} Folien</span>}
              {(pres.tokens_in > 0 || pres.tokens_out > 0) && (
                <span title="Pfeil-Richtung aus User-Sicht: ↑ = an LLM gesendet (Prompt), ↓ = von LLM erhalten (Antwort)">
                  ↑{pres.tokens_in.toLocaleString()} ↓
                  {pres.tokens_out.toLocaleString()} tok
                </span>
              )}
              {cost && cost.total_eur > 0 && (
                <span
                  title={
                    `Text: ${cost.text_cost_eur.toFixed(4)} EUR ` +
                    `(${cost.text_tokens_in.toLocaleString()} in + ${cost.text_tokens_out.toLocaleString()} out, ${cost.text_model ?? "?"})\n` +
                    `Imagen: ${cost.image_gen_cost_eur.toFixed(4)} EUR (${cost.image_gen_count} calls)\n` +
                    `Audit: ${cost.image_audit_cost_eur.toFixed(4)} EUR (${cost.image_audit_count} calls)`
                  }
                  className="font-mono text-text"
                >
                  €{cost.total_eur.toFixed(3)}
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            {manifest.has_pdf && (
              <Button variant="secondary" onClick={downloadPdf}>
                PDF
              </Button>
            )}
            {manifest.has_pptx && (
              <Button variant="primary" onClick={downloadPptx}>
                PPTX
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={regenerate}
              loading={regenerating}
              disabled={!manifest.has_pptx}
              title="Vorschau neu erzeugen"
            >
              ↻ Vorschau
            </Button>
          </div>
        </div>
        {/* Tabs */}
        <div className="mt-4 flex gap-6 border-b border-border">
          {(["preview", "slides", "audit"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`-mb-px border-b-2 px-1 py-2 text-sm font-medium transition ${
                tab === t
                  ? "border-accent-500 text-text"
                  : "border-transparent text-text-2 hover:text-text"
              }`}
            >
              {t === "preview"
                ? "Vorschau"
                : t === "slides"
                  ? "Folien (Spec)"
                  : "Audit-Trace"}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {tab === "preview" && (
          <>
            {!previewReady && (
              <EmptyState
                icon={<ImageIcon className="w-6 h-6" />}
                title={
                  pres.status === "done"
                    ? "Vorschau wird erzeugt …"
                    : "Noch keine Vorschau gerendert"
                }
                hint={
                  manifest.has_pptx
                    ? "Erzeuge eine Bild-Vorschau der Folien."
                    : "Die Vorschau wird angezeigt, sobald die Präsentation gerendert ist."
                }
                action={
                  manifest.has_pptx && pres.status !== "done" ? (
                    <Button onClick={regenerate} loading={regenerating}>
                      Vorschau erzeugen
                    </Button>
                  ) : undefined
                }
              />
            )}
            {previewReady && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                  <PageThumbnail
                    key={n}
                    presId={pres.id}
                    pageN={n}
                    onClick={() => setLightboxPage(n)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {tab === "slides" && slidesSpec && (
          <div className="space-y-3">
            {slidesSpec.map((s: unknown, i: number) => {
              const slide = s as {
                position?: number;
                template?: string;
                spec?: { title?: string };
              };
              return (
                <div
                  key={i}
                  className="rounded-lg border border-border bg-surface p-4"
                >
                  <div className="mb-2 flex items-center gap-2 text-xs text-text-2">
                    <span className="font-mono">#{slide.position ?? i + 1}</span>
                    <span className="rounded-md bg-surface-3 px-1.5 py-0.5">
                      {slide.template}
                    </span>
                  </div>
                  <div className="font-medium text-text">
                    {slide.spec?.title ?? "(kein Titel)"}
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-text-2 hover:text-text">
                      Spec-JSON
                    </summary>
                    <pre className="mt-2 overflow-auto rounded-md bg-surface-2 p-3 text-xs">
                      {JSON.stringify(slide.spec, null, 2)}
                    </pre>
                  </details>
                </div>
              );
            })}
          </div>
        )}

        {tab === "audit" && gateReport && (
          <div className="space-y-2">
            <div className="mb-3 text-xs text-text-2">
              Substance-Gate-Report — pro Slide: Validator-Errors,
              Substance-Score, Re-Gen-Attempts, Template-Downgrades
            </div>
            {gateReport.map((r: unknown, i: number) => {
              const rep = r as {
                position?: number;
                template?: string;
                validator_errors?: Array<{ field: string; rule: string }>;
                audit_score?: number | null;
                excerpt_fidelity?: number | null;
                regen_attempts?: number;
                final_score?: number | null;
                downgraded_from?: string | null;
              };
              const score = rep.final_score ?? rep.audit_score;
              return (
                <div
                  key={i}
                  className="rounded-md border border-border bg-surface p-3 text-sm"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-text-2">
                      #{rep.position}
                    </span>
                    <span className="rounded-md bg-surface-3 px-1.5 py-0.5 text-xs">
                      {rep.template}
                    </span>
                    {score != null && (
                      <StatusBadge
                        status={
                          score >= 7 ? "success" : score >= 5 ? "warning" : "danger"
                        }
                      >
                        {score}/10
                      </StatusBadge>
                    )}
                    {rep.excerpt_fidelity != null && (
                      <span className="text-xs text-text-2">
                        fidelity {rep.excerpt_fidelity}/5
                      </span>
                    )}
                    {(rep.regen_attempts ?? 0) > 0 && (
                      <span className="text-xs text-warning">
                        {rep.regen_attempts}× regen
                      </span>
                    )}
                    {rep.downgraded_from && (
                      <span className="text-xs text-danger">
                        downgrade von {rep.downgraded_from}
                      </span>
                    )}
                  </div>
                  {(rep.validator_errors?.length ?? 0) > 0 && (
                    <div className="mt-2 space-y-0.5 text-xs text-danger">
                      {rep.validator_errors!.map((e, j) => (
                        <div key={j}>
                          <span className="font-mono">{e.field}</span>:{" "}
                          {e.rule}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "audit" && !gateReport && (
          <div className="text-sm text-text-2">
            Kein Substance-Gate-Report (Pres wurde vor Substance-v2 erstellt).
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxPage != null && (
        <Lightbox
          presId={pres.id}
          pageN={lightboxPage}
          total={pageCount}
          onClose={() => setLightboxPage(null)}
          onPrev={() => setLightboxPage((p) => (p && p > 1 ? p - 1 : p))}
          onNext={() =>
            setLightboxPage((p) => (p && p < pageCount ? p + 1 : p))
          }
        />
      )}
    </div>
  );
}
